import { useConfigurations } from "@via/api/configuration.tsx";
import EntityTagsSection from "@via/components/entity-tags-section.tsx";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  useCreatePipelineMenuGroup,
  usePipelineMenuGroupsList,
  useEditPipelineMenuGroup,
  useDeletePipelineMenuGroup,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { getPermissionsEncoding } from "@via/pages/projects/runs/utils/helpers.tsx";
import { ROUTES, USER_ROLES } from "@via/utils/constants.tsx";
import { checkIsSemanticVersion, classNames } from "@via/utils/helpers.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { Permissions, PermissionType, TaggableEntityType } from "common";
import { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { useAbility } from "@via/context/ability-context";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { EditProcessToolbar } from "@via/pages/projects/pipelines/workflow/components/edit-process-toolbar.tsx";
import {
  EditProcessGroupOptions,
  EditProcessGroupTypes,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import Modal from "@via/components/modal";
import ManageMenuGroupModal from "@via/pages/projects/pipelines/workflow/components/manage-menu-group-modal.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

export default function PipelineSettings(): JSX.Element {
  const { user } = useAuth();
  const { setNotification } = useNotifications();
  const { mainPipeline, pipelineDraft, setPipelineDraft } = useGlobal();
  const [menuGroups, setMenuGroups] = useState<SelectOption[]>([]);
  const [selectedMenuGroup, setSelectedMenuGroup] =
    useState<SelectOption | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<string | null>(null);

  const { data: configurations } = useConfigurations();
  const defaultPermission = configurations?.ENABLE_SHARE_WITH_EVERYONE_PIPELINE
    ? PermissionType.Public
    : PermissionType.UserOwned;
  const [permissions, setPermissions] =
    useState<PermissionType>(defaultPermission);
  const { data: menuGroupsListData, isSuccess: isMenuGroupsListSuccess } =
    usePipelineMenuGroupsList();
  const {
    mutate: createNewMenuGroup,
    data: menuGroupData,
    isSuccess: isMenuGroupsSuccess,
  } = useCreatePipelineMenuGroup();
  const {
    mutate: editMenuGroup,
    data: editedMenuGroupData,
    isSuccess: isEditMenuGroupSuccess,
  } = useEditPipelineMenuGroup();
  const {
    mutate: deleteMenuGroup,
    data: deletedMenuGroupData,
    isSuccess: isDeleteMenuGroupSuccess,
  } = useDeletePipelineMenuGroup();

  const { increaseChangeCount } = useChangesDetect();
  const ability = useAbility([
    {
      action: "Submit",
      subject: "Button",
      condition:
        user?.userRole === USER_ROLES.ADMIN
          ? true
          : pipelineDraft?.isOwner &&
            configurations?.ENABLE_SHARE_WITH_EVERYONE_PIPELINE,
    },
  ]);

  useEffect(() => {
    if (pipelineDraft) {
      const perms = getPermissionsEncoding(pipelineDraft?.permission);
      setPermissions(perms);
    }
  }, [pipelineDraft?.permission]);

  useEffect(() => {
    if (menuGroupsListData) {
      const { data } = menuGroupsListData;
      const groups = data?.map(({ name, id }) => ({
        label: name,
        value: id,
      }));

      const match = data?.find(
        group => group.id === pipelineDraft?.pipelineGroupId
      );
      const selected = match ? { label: match.name, value: match.id } : null;

      setSelectedMenuGroup(selected);
      setMenuGroups(groups);
    }
  }, [isMenuGroupsListSuccess, menuGroupsListData]);

  useEffect(() => {
    // Handling after a new menu group is created
    if (menuGroupData) {
      const { name, id } = menuGroupData;
      const newMenuGroup = { label: name, value: id };
      setPipelineDraft(prevData => ({
        ...prevData,
        pipelineGroupId: id,
      }));

      setSelectedMenuGroup(newMenuGroup);
      setMenuGroups(oldOptions => [...oldOptions, newMenuGroup]);
      setOpenModal(false);
    }
  }, [isMenuGroupsSuccess, menuGroupData]);

  useEffect(() => {
    // Handling after a menu group is edited
    if (isEditMenuGroupSuccess) {
      if (!editedMenuGroupData) {
        return;
      }
      const { name, id } = editedMenuGroupData;
      const updatedMenuGroup = { label: name, value: id };
      setSelectedMenuGroup(updatedMenuGroup);
      setOpenModal(false);
    }
  }, [isEditMenuGroupSuccess, editedMenuGroupData]);

  useEffect(() => {
    // Handling after a menu group is deleted
    if (isDeleteMenuGroupSuccess) {
      const menuGroup = menuGroups.find(
        group => group.value === pipelineDraft?.pipelineGroupId
      );
      if (!menuGroup) {
        const selected = menuGroups[0];
        setSelectedMenuGroup(selected);
        setPipelineDraft(prevData => ({
          ...prevData,
          pipelineGroupId: selected.value,
        }));
      }
    }
  }, [isDeleteMenuGroupSuccess, deletedMenuGroupData, menuGroups]);

  const handleCreatePipelineMenuGroup = (name: string): void => {
    increaseChangeCount();
    createNewMenuGroup({ name });
  };

  const handleEditPipelineMenuGroup = formPayload => {
    const { menuGroup, name } = formPayload;
    const payload = { value: menuGroup, rename: name };
    editMenuGroup(payload);
    setOpenModal(false);
  };

  const handleDeletePipelineMenuGroup = selected => {
    deleteMenuGroup(selected);
    setOpenModal(false);
  };

  const handleMenuGroupIdChange = selected => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      pipelineGroupId: selected.value,
    }));
    setSelectedMenuGroup(selected);
  };

  const handleCheckVersion = e => {
    increaseChangeCount();
    const isValidVersion = checkIsSemanticVersion(e.target.value, true);
    if (!isValidVersion) {
      setNotification({
        id: "pipeline-version-invalid",
        message: "Invalid Version Detected",
        details:
          "Acceptable Versions Must Be Semantic (x.x.x). Modify the Pipeline Version field in Settings.",
        variant: NOTIFICATION.ERROR,
      });
      setPipelineDraft(prevData => ({
        ...prevData,
        version: mainPipeline?.version,
      }));
    }
  };

  const handleVersionChange = e => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      version: e.target.value,
    }));
  };

  const handlePinChange = () => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      pin: !prevData.pin,
    }));
  };

  const handlePubliclySearchableChange = () => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      publiclySearchable: !prevData.publiclySearchable,
    }));
  };

  const handlePinOrderChange = e => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      pinOrder: e.target.value,
    }));
  };

  /** Updating Permissions Handlers **/
  const handlePermissionUpdate = (type, value) => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      permission: value,
      groupId: value === Permissions.UserOwned ? null : prevData?.groupId,
    }));
    setPermissions(type);
  };

  const handleGroupIdUpdate = selected => {
    increaseChangeCount();
    setPipelineDraft(prevData => ({
      ...prevData,
      permission: Permissions.GroupShared,
      groupId:
        prevData.permission === Permissions.GroupShared ? selected.value : null,
    }));

    if (selected.value && permissions !== PermissionType.GroupShared) {
      setPermissions(PermissionType.GroupShared);
    }
  };

  const handleWriteGroupIdUpdate = selected => {
    increaseChangeCount();
    const writeGroupIds = selected.map(option => option.value);
    setPipelineDraft(prevData => ({
      ...prevData,
      writeGroupIds,
    }));
  };

  const onCreateMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.CreateMenuGroup);
    setOpenModal(true);
  };

  const onEditMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.EditMenuGroup);
    setOpenModal(true);
  };

  const onDeleteMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.DeleteMenuGroup);
    setOpenModal(true);
  };

  const modals = useMemo(
    () => ({
      [EditProcessGroupOptions.CreateMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.CreateMenuGroup}
          menuGroups={menuGroups}
          onCancel={() => setOpenModal(false)}
          onSubmit={handleCreatePipelineMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
      [EditProcessGroupOptions.EditMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.EditMenuGroup}
          menuGroups={menuGroups}
          onCancel={() => setOpenModal(false)}
          onSubmit={handleEditPipelineMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
      [EditProcessGroupOptions.DeleteMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.DeleteMenuGroup}
          menuGroups={menuGroups}
          onCancel={() => setOpenModal(false)}
          onSubmit={handleDeletePipelineMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
    }),
    [modalContent, menuGroups, menuGroupsListData, selectedMenuGroup]
  );

  const getPipelineSettingsContext = useCallback(() => {
    return {
      pipeline: {
        id: pipelineDraft?.id,
        name: pipelineDraft?.name,
        version: pipelineDraft?.version,
        isEditingAllowed: pipelineDraft?.isEditingAllowed,
        isOwner: pipelineDraft?.isOwner,
        isAdmin: pipelineDraft?.isAdmin,
      },
      settings: {
        menuGroup: selectedMenuGroup
          ? {
              id: selectedMenuGroup.value,
              name: selectedMenuGroup.label,
            }
          : null,
        availableMenuGroups: menuGroups.map(group => ({
          id: group.value,
          name: group.label,
        })),
        permissions: {
          type: permissions,
          permission: pipelineDraft?.permission,
          groupId: pipelineDraft?.groupId,
          writeGroupIds: pipelineDraft?.writeGroupIds || [],
        },
        adminSettings:
          user.userRole === USER_ROLES.ADMIN
            ? {
                publiclySearchable: pipelineDraft?.publiclySearchable || false,
                pin: pipelineDraft?.pin || false,
                pinOrder: pipelineDraft?.pinOrder || null,
              }
            : null,
      },
    };
  }, [
    pipelineDraft,
    selectedMenuGroup,
    menuGroups,
    permissions,
    user.userRole,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.PIPELINE_SETTINGS,
    contextName: "Pipeline Settings",
    getContext: getPipelineSettingsContext,
    systemPrompt:
      "You are analyzing pipeline settings page. Help answer questions about pipeline settings, permissions, menu groups, version, and admin settings.",
  });

  return (
    <div className="flex h-[74dvh] flex-row justify-start">
      <div className="flex w-1/2 flex-col border-r-[1px] pr-8">
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Choose Menu Group
        </label>
        <div className="flex">
          <div className="w-3/4">
            <CreatableSelect
              name="menuGroup"
              isMulti={false}
              isDisabled={!pipelineDraft?.isEditingAllowed}
              onChange={handleMenuGroupIdChange}
              value={selectedMenuGroup}
              onCreateOption={handleCreatePipelineMenuGroup}
              options={menuGroups}
              classNamePrefix={"select"}
              placeholder={"Select a Menu Group"}
            />
          </div>
          <EditProcessToolbar
            type={EditProcessGroupTypes.MenuGroup}
            readOnly={!pipelineDraft?.isEditingAllowed}
            onCreate={onCreateMenuGroup}
            onEdit={onEditMenuGroup}
            onDelete={onDeleteMenuGroup}
          />
        </div>
        <Modal open={openModal} setOpen={setOpenModal} showButtons={false}>
          {modalContent && modals[modalContent]}
        </Modal>
        <div className="pt-3">
          <label className="block pt-2 text-sm font-medium leading-6 text-gray-900">
            Choose Pipeline Version
          </label>
          <input
            value={pipelineDraft?.version}
            disabled={!pipelineDraft?.isEditingAllowed}
            onChange={handleVersionChange}
            onBlur={handleCheckVersion}
            placeholder="Enter Pipeline Version (e.g. 1.0.0)"
            name="Pipeline Version"
            type="text"
            className="mr-1 mt-2 block w-48 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        {user.userRole === USER_ROLES.ADMIN && (
          <div className="pt-3">
            <label className="block pt-2 text-sm font-medium leading-6 text-gray-900">
              Admin Settings
            </label>
            <div className="mt-3 flex flex-row items-center">
              <label className="block pr-4 text-sm leading-6 text-gray-900">
                Publicly Searchable
              </label>
              <IndeterminateCheckbox
                checked={pipelineDraft?.publiclySearchable}
                onChange={handlePubliclySearchableChange}
                onClick={e => {
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="mt-3 flex flex-row items-center">
              <label className="block pr-4 text-sm leading-6 text-gray-900">
                Pin to Main Page
              </label>
              <IndeterminateCheckbox
                checked={pipelineDraft?.pin}
                onChange={handlePinChange}
                onClick={e => {
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="mt-2 flex flex-row items-center">
              <label className="block pr-4 text-sm leading-6 text-gray-900">
                Pin Position
              </label>
              <input
                className="mr-1 block rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                disabled={!pipelineDraft?.pin}
                defaultValue={pipelineDraft?.pinOrder}
                onBlur={handlePinOrderChange}
                placeholder="Enter Pin Order"
                name="Pin Order"
                type="text"
              />
            </div>
          </div>
        )}
      </div>
      <fieldset className="w-1/2 pl-8">
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Set Permissions
        </label>
        <div className="mt-2 space-y-4">
          <div className="relative flex items-start">
            <div className="absolute flex h-6 items-center">
              <input
                id="permission-public"
                disabled={
                  !pipelineDraft?.isEditingAllowed ||
                  !ability.can("Submit", "Button")
                }
                checked={permissions === PermissionType.Public}
                onChange={() =>
                  handlePermissionUpdate(
                    PermissionType.Public,
                    Permissions.Public
                  )
                }
                value={PermissionType.Public}
                aria-describedby="permission-public-description"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div
              className={classNames(
                !pipelineDraft?.isEditingAllowed ||
                  !ability.can("Submit", "Button")
                  ? "opacity-50"
                  : "",
                "pl-7 text-sm leading-6"
              )}>
              <label
                htmlFor="permission-public"
                className="font-medium text-gray-900">
                Public access
              </label>
              <p id="permission-public-description" className="text-gray-500">
                Everyone with the link will see this pipeline.
              </p>
            </div>
          </div>

          <div>
            <div className="relative flex items-start">
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-group"
                  disabled={!pipelineDraft?.isEditingAllowed}
                  checked={permissions === PermissionType.GroupShared}
                  onChange={() =>
                    handlePermissionUpdate(
                      PermissionType.GroupShared,
                      Permissions.GroupShared
                    )
                  }
                  value={PermissionType.GroupShared}
                  aria-describedby="permission-group-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div
                className={classNames(
                  !pipelineDraft?.isEditingAllowed ? "opacity-50" : "",
                  "pl-7 text-sm leading-6"
                )}>
                <label
                  htmlFor="permission-group"
                  className="font-medium text-gray-900">
                  Private to group members
                </label>
                <p id="permission-group-description" className="text-gray-500">
                  Only members of this pipeline would be able to access.
                </p>
              </div>
            </div>
          </div>
          {permissions === PermissionType.GroupShared && (
            <UserGroupSelectComponent
              name="groupId"
              isDisabled={!pipelineDraft?.isEditingAllowed}
              menuPlacement="top"
              {...(pipelineDraft?.groupId
                ? { missingId: [pipelineDraft.groupId] }
                : [])}
              onChange={handleGroupIdUpdate}
            />
          )}
          <div>
            <div className="relative flex items-start">
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-private"
                  disabled={!pipelineDraft?.isEditingAllowed}
                  checked={permissions === PermissionType.UserOwned}
                  value={PermissionType.UserOwned}
                  onChange={() =>
                    handlePermissionUpdate(
                      PermissionType.UserOwned,
                      Permissions.UserOwned
                    )
                  }
                  aria-describedby="permission-private-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div
                className={classNames(
                  !pipelineDraft?.isEditingAllowed ? "opacity-50" : "",
                  "pl-7 text-sm leading-6"
                )}>
                <label
                  htmlFor="permission-private"
                  className="font-medium text-gray-900">
                  Private to you
                </label>
                <p
                  id="permission-private-description"
                  className="text-gray-500">
                  You are the only one able to access this pipeline.
                </p>
              </div>
            </div>
          </div>
        </div>
        <hr className="my-4" />
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Group Permission to Write
        </label>
        <div className="mt-2 space-y-4">
          <UserGroupSelectComponent
            name="writeGroupId"
            isDisabled={!pipelineDraft?.isEditingAllowed}
            isMulti={true}
            {...(pipelineDraft?.writeGroupIds
              ? { missingId: pipelineDraft.writeGroupIds }
              : [])}
            onChange={handleWriteGroupIdUpdate}
          />
        </div>
        {pipelineDraft?.id && (
          <EntityTagsSection
            entityType={TaggableEntityType.Pipeline}
            entityId={String(pipelineDraft.id)}
            canEdit={!!pipelineDraft?.isEditingAllowed}
          />
        )}
      </fieldset>
    </div>
  );
}
